import { createRealtimeSession } from '../utils/agentApiUtils';

// GA Realtime API: browser POSTs its SDP offer to /realtime/calls using the
// ephemeral token. The model is bound to the token server-side, so no query.
const OPENAI_REALTIME_CALLS_URL = 'https://api.openai.com/v1/realtime/calls';

/** Wait for session.created before greeting; fallback if that event never arrives. */
const GREETING_FALLBACK_MS = 3000;
/** Recover to listening if a greeting response never produces audio. */
const GREETING_RESPONSE_TIMEOUT_MS = 20000;
/** Ignore VAD / new turns briefly after Spacey speaks (echo guard). */
const ASSISTANT_ECHO_COOLDOWN_MS = 1800;
/** Min chars for a normal assistant bubble (skip obvious cut-off fragments). */
const MIN_ASSISTANT_CAPTION_CHARS = 12;

/** Instructions for Spacey's one-time spoken hello when voice mode connects. */
const buildVoiceGreetingInstructions = (userContext = {}) => {
  const name = (userContext.userName || '').trim().split(/\s+/)[0];
  const nameHint = name
    ? `Address them as ${name}. `
    : '';
  return (
    `${nameHint}Greet the user now in exactly two short spoken sentences. `
    + 'You are Spacey from MillionSpaces — warm, friendly, and a little playful, like a natural voice chat. '
    + 'Say hi, mention you help find workspaces in Sri Lanka, and ask what they are looking for. '
    + 'Sound relaxed and human, not scripted. Do not call any tools.'
  );
};

const responseHasAudioOutput = (response) => {
  if (!Array.isArray(response?.output)) return false;
  return response.output.some((item) => {
    if (item?.type !== 'message' || !Array.isArray(item.content)) return false;
    return item.content.some((part) => String(part?.type || '').includes('audio'));
  });
};

/**
 * RealtimeVoiceClient — Phase 0
 *
 * Manages a live, hands-free voice conversation with OpenAI's Realtime API
 * over WebRTC. Audio flows directly browser <-> OpenAI (lowest latency); our
 * backend only mints the short-lived ephemeral session token.
 *
 * Phase 0 scope: plain spoken conversation (no tool calling yet). Tool calls
 * (search_available_spaces, etc.) are wired in Phase 1.
 *
 * Lifecycle callbacks (all optional):
 *   onStatus(status)  - 'connecting' | 'live' | 'closed' | 'error'
 *   onPhase(phase)    - 'listening' | 'thinking' | 'searching' | 'speaking'
 *   onError(error)
 *   onEvent(event)    - raw realtime data-channel events (debug / future use)
 *   executeToolCall(name, args) => Promise<result> - runs a tool call on the
 *       backend; `result.modelOutput` is fed back to the model, the rest is UI.
 *   onToolResult(result, {name, args}) - lets the UI render cards/actions.
 *   onCaption({ role, text, final }) - live transcript for user/assistant.
 *   onLevel(level) - 0..1 audio amplitude for the reactive orb.
 */
export default class RealtimeVoiceClient {
  constructor({
    onStatus, onPhase, onError, onEvent, executeToolCall, onToolResult, onCaption, onLevel,
  } = {}) {
    this.onStatus = onStatus || (() => {});
    this.onPhase = onPhase || (() => {});
    this.onError = onError || (() => {});
    this.onEvent = onEvent || (() => {});
    this.executeToolCall = executeToolCall || null;
    this.onToolResult = onToolResult || (() => {});
    this.onCaption = onCaption || (() => {});
    this.onLevel = onLevel || (() => {});

    this.pc = null;
    this.dc = null;
    this.micStream = null;
    this.audioEl = null;
    this.closed = false;
    this.handledCallIds = new Set();
    this.assistantTranscript = '';
    this.phase = 'listening';
    this.userMuted = false;
    this.userContext = {};
    this.greetingSent = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    this.greetingFallbackTimer = null;
    this.greetingResponseTimer = null;
    this.activeResponseId = null;
    this.greetingResponseId = null;
    /** Deferred connect-greeting caption — emitted once, after playback finishes. */
    this.pendingGreetingCaption = null;

    this.greetingPlaybackFallbackTimer = null;
    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    this.levelRaf = null;

    /** responseId → 'in_progress' | 'completed' | 'cancelled' | 'failed' */
    this.responseStatuses = new Map();
    this.committedResponseIds = new Set();
    /** Deferred assistant caption until response completes + playback stops. */
    this.pendingAssistantCommit = null;
    this.assistantEchoCooldownUntil = 0;
    /** Set before client-initiated response.create; cleared on response.created. */
    this.expectingClientResponse = false;
    /** Skip post-playback echo cooldown when the user intentionally barged in. */
    this.userBargeInActive = false;
    this.echoCooldownTimer = null;
  }

  static isSupported() {
    return (
      typeof window !== 'undefined'
      && typeof window.RTCPeerConnection === 'function'
      && !!navigator?.mediaDevices?.getUserMedia
    );
  }

  async start(userContext = {}, voice) {
    if (!RealtimeVoiceClient.isSupported()) {
      const err = new Error('Live voice mode is not supported in this browser.');
      this.onError(err);
      this.onStatus('error');
      throw err;
    }

    this.userContext = userContext || {};
    this.greetingSent = false;
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = true;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.responseStatuses = new Map();
    this.committedResponseIds = new Set();
    this.pendingAssistantCommit = null;
    this.assistantEchoCooldownUntil = 0;
    this.expectingClientResponse = false;
    this.userBargeInActive = false;
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
      this.echoCooldownTimer = null;
    }

    try {
      this.onStatus('connecting');
      this.setPhase('speaking');

      // 1. Mint an ephemeral session from our backend.
      // GA response: top-level `value` (ek_...). Older shape: client_secret.value.
      const session = await createRealtimeSession(userContext, voice);
      if (this.closed) return;
      const ephemeralKey = session?.value || session?.client_secret?.value;
      if (!ephemeralKey) {
        throw new Error('No ephemeral key returned from realtime-session.');
      }

      // 2. Peer connection + remote audio sink.
      const pc = new RTCPeerConnection();
      this.pc = pc;

      this.audioEl = document.createElement('audio');
      this.audioEl.autoplay = true;
      this.audioEl.setAttribute('playsinline', 'true');
      this.audioEl.playsInline = true;
      pc.ontrack = (event) => {
        if (this.audioEl && event.streams && event.streams[0]) {
          this.audioEl.srcObject = event.streams[0];
          this.attachRemoteAnalyser(event.streams[0]);
        }
      };

      pc.onconnectionstatechange = () => {
        if (this.closed) return;
        const state = pc.connectionState;
        if (state === 'connected') {
          this.onStatus('live');
          // Stay in speaking until the connect greeting finishes — not listening.
          if (this.awaitingConnectGreeting) {
            this.setPhase('greeting');
          } else {
            this.setPhase('listening');
          }
        } else if (state === 'failed' || state === 'disconnected') {
          this.onStatus('error');
        }
      };

      // 3. Microphone.
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (this.closed) { this.releaseResources(); return; }
      this.micStream.getTracks().forEach((track) => pc.addTrack(track, this.micStream));
      this.attachMicAnalyser(this.micStream);
      this.startLevelMetering();
      try {
        const ctx = this.ensureAudioContext();
        if (ctx?.state === 'suspended') await ctx.resume();
      } catch { /* best-effort for iOS */ }

      // 4. Data channel for realtime events.
      const dc = pc.createDataChannel('oai-events');
      this.dc = dc;
      dc.addEventListener('message', (e) => this.handleServerEvent(e));

      // 5. SDP offer/answer handshake with OpenAI.
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      if (this.closed) { this.releaseResources(); return; }

      const sdpResponse = await fetch(OPENAI_REALTIME_CALLS_URL, {
        method: 'POST',
        body: offer.sdp,
        headers: {
          Authorization: `Bearer ${ephemeralKey}`,
          'Content-Type': 'application/sdp',
        },
      });

      if (!sdpResponse.ok) {
        throw new Error(`Realtime SDP exchange failed: ${sdpResponse.status}`);
      }

      const answerSdp = await sdpResponse.text();
      if (this.closed) { this.releaseResources(); return; }
      await pc.setRemoteDescription({ type: 'answer', sdp: answerSdp });
      this.scheduleGreetingFallback();
    } catch (error) {
      // Surface the real error instead of masking it as "Ended".
      this.releaseResources();
      this.onError(error);
      this.onStatus('error');
      throw error;
    }
  }

  handleServerEvent(messageEvent) {
    let event;
    try {
      event = JSON.parse(messageEvent.data);
    } catch {
      return;
    }
    this.onEvent(event);

    // Map realtime events to orb phases.
    switch (event.type) {
      case 'session.created': {
        this.clearGreetingFallbackTimer();
        this.sendVoiceGreeting();
        break;
      }
      case 'session.updated':
        // Ignore — greeting is sent once on session.created only.
        break;
      case 'error':
        // eslint-disable-next-line no-console
        console.warn('[voice] realtime error:', event);
        if (this.pendingGreetingResponse) {
          this.clearGreetingResponseTimer();
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          this.greetingResponseId = null;
          this.pendingGreetingCaption = null;
          this.clearGreetingPlaybackFallbackTimer();
          this.assistantTranscript = '';
          // Allow one retry if the greeting request was rejected (e.g. bad params).
          this.greetingSent = false;
        } else if (this.awaitingConnectGreeting) {
          this.finishConnectGreeting();
        } else if (this.phase === 'thinking') {
          this.setPhase('listening');
        }
        break;
      case 'input_audio_buffer.speech_started':
        if (this.canUserBargeIn()) {
          this.handleUserBargeIn();
          break;
        }
        if (this.shouldIgnoreVadTurn()) break;
        break;
      case 'input_audio_buffer.speech_stopped':
        if (this.shouldIgnoreVadTurn()) break;
        if (!this.pendingGreetingResponse) {
          this.setPhase('thinking');
        }
        break;
      case 'response.created':
        if (event.response?.id) {
          const responseId = event.response.id;
          if (this.pendingGreetingResponse && !this.greetingResponseId) {
            this.greetingResponseId = responseId;
          } else if (
            this.isConnectGreeting()
            && this.greetingResponseId
            && responseId !== this.greetingResponseId
          ) {
            this.sendEvent({ type: 'response.cancel' });
            break;
          } else if (this.shouldCancelStrayResponse()) {
            this.responseStatuses.set(responseId, 'cancelled');
            this.sendEvent({ type: 'response.cancel' });
            break;
          }
          this.expectingClientResponse = false;
          this.activeResponseId = responseId;
          this.responseStatuses.set(responseId, 'in_progress');
        }
        if (!this.pendingGreetingResponse) {
          this.setPhase('thinking');
        }
        break;
      // GA + legacy audio output events
      case 'response.audio.delta':
      case 'response.output_audio.delta':
      case 'response.output_audio.done':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
          this.setPhase('greeting');
        } else {
          this.setPhase('speaking');
        }
        break;
      case 'output_audio_buffer.started':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        } else if (this.pendingAssistantCommit) {
          this.pendingAssistantCommit.playbackStopped = false;
        }
        this.resumeAssistantPlayback();
        if (this.pendingGreetingResponse) {
          this.setPhase('greeting');
        } else {
          this.setPhase('speaking');
        }
        break;
      // GA + legacy assistant transcript events
      case 'response.audio_transcript.delta':
      case 'response.output_audio_transcript.delta':
        if (this.pendingGreetingResponse) {
          this.greetingAudioHeard = true;
          this.clearGreetingResponseTimer();
        }
        if (typeof event.delta === 'string') {
          this.assistantTranscript += event.delta;
          this.onCaption({ role: 'assistant', text: this.assistantTranscript, final: false });
        }
        break;
      case 'response.audio_transcript.done':
      case 'response.output_audio_transcript.done': {
        const finalText = event.transcript || this.assistantTranscript;
        const responseId = event.response_id || event.response?.id || this.activeResponseId;
        if (this.pendingGreetingResponse) {
          if (finalText) this.pendingGreetingCaption = finalText;
        } else if (finalText && responseId) {
          this.stageAssistantCaption(responseId, finalText);
        }
        this.assistantTranscript = '';
        break;
      }
      case 'conversation.item.input_audio_transcription.delta':
        if (this.shouldForwardUserCaption() && typeof event.delta === 'string' && event.delta) {
          this.onCaption({ role: 'user', text: event.delta, final: false });
        }
        break;
      case 'conversation.item.input_audio_transcription.completed':
        if (this.shouldForwardUserCaption() && event.transcript) {
          this.onCaption({ role: 'user', text: event.transcript, final: true });
        }
        break;
      // Function-call events (GA emits these in addition to response.done)
      case 'response.function_call_arguments.done':
        this.runToolCall({
          name: event.name,
          call_id: event.call_id,
          arguments: event.arguments,
        });
        break;
      case 'response.output_item.done':
        if (event.item?.type === 'function_call') {
          this.runToolCall(event.item);
        }
        break;
      case 'response.done': {
        const response = event.response;
        const responseId = response?.id || this.activeResponseId;
        if (responseId) {
          const status = response?.status === 'completed' ? 'completed'
            : response?.status === 'cancelled' ? 'cancelled'
              : response?.status === 'failed' ? 'failed'
                : 'completed';
          this.responseStatuses.set(responseId, status);
          if (status === 'cancelled' || status === 'failed') {
            if (this.pendingAssistantCommit?.id === responseId) {
              this.pendingAssistantCommit = null;
            }
          }
        }
        this.activeResponseId = null;
        const hasToolCall = Array.isArray(response?.output)
          && response.output.some((item) => item?.type === 'function_call');
        if (this.pendingGreetingResponse) {
          if (hasToolCall) {
            this.pendingGreetingResponse = false;
            this.clearGreetingResponseTimer();
          } else if (response?.status === 'failed' || response?.status === 'cancelled') {
            this.recoverFromIncompleteGreeting();
          } else if (responseHasAudioOutput(response) || this.greetingAudioHeard) {
            this.greetingAudioHeard = true;
            this.clearGreetingResponseTimer();
            this.scheduleGreetingPlaybackFallback();
          }
        } else if (
          (response?.status === 'failed' || response?.status === 'cancelled')
          && this.phase === 'thinking'
        ) {
          this.setPhase('listening');
        } else if (responseId && response?.status === 'completed') {
          if (this.pendingAssistantCommit?.id === responseId) {
            this.pendingAssistantCommit.responseCompleted = true;
          }
          this.tryCommitAssistantCaption();
        }
        this.handleFunctionCalls(response);
        break;
      }
      case 'output_audio_buffer.stopped':
        if (this.pendingGreetingResponse) {
          this.pendingGreetingResponse = false;
          this.greetingAudioHeard = false;
          this.clearGreetingResponseTimer();
          this.clearGreetingPlaybackFallbackTimer();
          this.commitConnectGreetingCaption();
          this.finishConnectGreeting();
          this.beginAssistantEchoCooldown();
        } else {
          const skipCooldown = this.userBargeInActive;
          this.userBargeInActive = false;
          if (!skipCooldown) {
            this.beginAssistantEchoCooldown();
          }
          if (this.pendingAssistantCommit) {
            this.pendingAssistantCommit.playbackStopped = true;
          }
          this.tryCommitAssistantCaption();
          this.setPhase('listening');
        }
        break;
      default:
        break;
    }
  }

  // ─── Audio-level metering (reactive orb) ───────────────────────────────────
  ensureAudioContext() {
    if (!this.audioCtx) {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (Ctx) this.audioCtx = new Ctx();
    }
    return this.audioCtx;
  }

  attachMicAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.micAnalyser = ctx.createAnalyser();
      this.micAnalyser.fftSize = 256;
      source.connect(this.micAnalyser);
    } catch { /* metering is best-effort */ }
  }

  attachRemoteAnalyser(stream) {
    try {
      const ctx = this.ensureAudioContext();
      if (!ctx) return;
      const source = ctx.createMediaStreamSource(stream);
      this.remoteAnalyser = ctx.createAnalyser();
      this.remoteAnalyser.fftSize = 256;
      source.connect(this.remoteAnalyser);
    } catch { /* metering is best-effort */ }
  }

  static rms(analyser) {
    if (!analyser) return 0;
    const buf = new Uint8Array(analyser.frequencyBinCount);
    analyser.getByteTimeDomainData(buf);
    let sum = 0;
    for (let i = 0; i < buf.length; i += 1) {
      const v = (buf[i] - 128) / 128;
      sum += v * v;
    }
    return Math.sqrt(sum / buf.length);
  }

  startLevelMetering() {
    const tick = () => {
      if (this.closed) return;
      const level = Math.max(
        RealtimeVoiceClient.rms(this.micAnalyser),
        RealtimeVoiceClient.rms(this.remoteAnalyser),
      );
      this.onLevel(Math.min(1, level * 2.2));
      this.levelRaf = window.requestAnimationFrame(tick);
    };
    this.levelRaf = window.requestAnimationFrame(tick);
  }

  // Sends a JSON event over the realtime data channel.
  sendEvent(event) {
    if (this.dc && this.dc.readyState === 'open') {
      this.dc.send(JSON.stringify(event));
    }
  }

  isInAssistantEchoCooldown() {
    return Date.now() < this.assistantEchoCooldownUntil;
  }

  beginAssistantEchoCooldown() {
    this.assistantEchoCooldownUntil = Date.now() + ASSISTANT_ECHO_COOLDOWN_MS;
    this.applyMicGate();
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
    }
    this.echoCooldownTimer = setTimeout(() => {
      this.echoCooldownTimer = null;
      this.applyMicGate();
    }, ASSISTANT_ECHO_COOLDOWN_MS);
  }

  shouldIgnoreVadTurn() {
    return this.isConnectGreeting()
      || this.phase === 'greeting'
      || this.phase === 'thinking'
      || this.phase === 'searching'
      || this.isInAssistantEchoCooldown();
  }

  canUserBargeIn() {
    return this.phase === 'speaking'
      && !this.isConnectGreeting()
      && !this.isInAssistantEchoCooldown();
  }

  shouldCancelStrayResponse() {
    return this.isInAssistantEchoCooldown() && !this.expectingClientResponse;
  }

  shouldForwardUserCaption() {
    return !this.isConnectGreeting()
      && (this.phase === 'listening' || this.phase === 'speaking')
      && !this.isInAssistantEchoCooldown();
  }

  stageAssistantCaption(responseId, text) {
    const trimmed = (text || '').trim();
    if (!trimmed || !responseId) return;
    this.pendingAssistantCommit = {
      id: responseId,
      text: trimmed,
      responseCompleted: false,
      playbackStopped: true,
    };
  }

  tryCommitAssistantCaption() {
    const pending = this.pendingAssistantCommit;
    if (!pending || this.committedResponseIds.has(pending.id)) return;

    const status = this.responseStatuses.get(pending.id);
    if (status === 'cancelled' || status === 'failed') {
      this.pendingAssistantCommit = null;
      return;
    }
    if (!pending.responseCompleted) return;
    if (!pending.playbackStopped) return;

    const text = pending.text;
    if (text.length < MIN_ASSISTANT_CAPTION_CHARS) return;

    this.committedResponseIds.add(pending.id);
    this.pendingAssistantCommit = null;
    this.onCaption({ role: 'assistant', text, final: true });
  }

  /** One-time spoken hello — only after OpenAI signals the session is ready. */
  sendVoiceGreeting() {
    if (this.greetingSent || this.closed) return;
    if (!this.dc || this.dc.readyState !== 'open') return;
    this.greetingSent = true;
    this.pendingGreetingResponse = true;
    this.greetingAudioHeard = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    this.setPhase('greeting');
    this.expectingClientResponse = true;
    this.sendEvent({
      type: 'response.create',
      response: {
        // GA Realtime: only ['audio'] or ['text'] — not both.
        output_modalities: ['audio'],
        instructions: buildVoiceGreetingInstructions(this.userContext),
      },
    });
    this.scheduleGreetingResponseTimeout();
  }

  isConnectGreeting() {
    return this.pendingGreetingResponse || this.awaitingConnectGreeting;
  }

  commitConnectGreetingCaption() {
    const text = (this.pendingGreetingCaption || this.assistantTranscript || '').trim();
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    if (text) {
      this.onCaption({ role: 'assistant', text, final: true });
    }
  }

  /** Connect greeting done — hand off to normal hands-free listening. */
  finishConnectGreeting() {
    this.awaitingConnectGreeting = false;
    this.greetingResponseId = null;
    this.setPhase('listening');
  }

  scheduleGreetingFallback() {
    this.clearGreetingFallbackTimer();
    this.greetingFallbackTimer = setTimeout(() => {
      this.greetingFallbackTimer = null;
      if (!this.closed && !this.greetingSent && this.dc?.readyState === 'open') {
        this.sendVoiceGreeting();
      }
    }, GREETING_FALLBACK_MS);
  }

  scheduleGreetingResponseTimeout() {
    this.clearGreetingResponseTimer();
    this.greetingResponseTimer = setTimeout(() => {
      this.greetingResponseTimer = null;
      if (!this.closed && this.pendingGreetingResponse && !this.greetingAudioHeard) {
        this.recoverFromIncompleteGreeting();
      }
    }, GREETING_RESPONSE_TIMEOUT_MS);
  }

  recoverFromIncompleteGreeting() {
    this.pendingGreetingResponse = false;
    this.greetingAudioHeard = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    this.assistantTranscript = '';
    this.clearGreetingResponseTimer();
    if (this.awaitingConnectGreeting) {
      this.finishConnectGreeting();
    } else if (this.phase === 'thinking' || this.phase === 'speaking') {
      this.setPhase('listening');
    }
  }

  clearGreetingFallbackTimer() {
    if (this.greetingFallbackTimer) {
      clearTimeout(this.greetingFallbackTimer);
      this.greetingFallbackTimer = null;
    }
  }

  clearGreetingResponseTimer() {
    if (this.greetingResponseTimer) {
      clearTimeout(this.greetingResponseTimer);
      this.greetingResponseTimer = null;
    }
  }

  scheduleGreetingPlaybackFallback() {
    this.clearGreetingPlaybackFallbackTimer();
    // iOS Safari may not emit output_audio_buffer.stopped — finish after playback window.
    this.greetingPlaybackFallbackTimer = setTimeout(() => {
      this.greetingPlaybackFallbackTimer = null;
      if (!this.closed && this.pendingGreetingResponse) {
        this.pendingGreetingResponse = false;
        this.clearGreetingResponseTimer();
        this.commitConnectGreetingCaption();
        this.finishConnectGreeting();
        this.beginAssistantEchoCooldown();
      }
    }, 8000);
  }

  clearGreetingPlaybackFallbackTimer() {
    if (this.greetingPlaybackFallbackTimer) {
      clearTimeout(this.greetingPlaybackFallbackTimer);
      this.greetingPlaybackFallbackTimer = null;
    }
  }

  clearGreetingTimers() {
    this.clearGreetingFallbackTimer();
    this.clearGreetingResponseTimer();
    this.clearGreetingPlaybackFallbackTimer();
  }

  // Detects function_call items in a completed response and runs them.
  handleFunctionCalls(response) {
    const output = response && Array.isArray(response.output) ? response.output : [];
    output
      .filter((item) => item && item.type === 'function_call')
      .forEach((item) => this.runToolCall(item));
  }

  async runToolCall(item) {
    const callId = item?.call_id;
    const toolName = item?.name;
    if (!callId || !toolName || this.handledCallIds.has(callId)) return;
    this.handledCallIds.add(callId);

    let args = {};
    try {
      args = item.arguments ? JSON.parse(item.arguments) : {};
    } catch {
      args = {};
    }

    // eslint-disable-next-line no-console
    console.log('[voice] function call:', toolName, args);

    let modelOutput = { error: 'Tool unavailable' };
    try {
      this.setPhase('searching');
      if (this.executeToolCall) {
        const result = await this.executeToolCall(toolName, args);
        if (this.closed) return;
        modelOutput = result?.modelOutput ?? {};
        // eslint-disable-next-line no-console
        console.log('[voice] tool exec result:', toolName, {
          spaces: result?.spaces?.length ?? 0,
          found: result?.modelOutput?.found,
        });
        this.onToolResult(result || {}, { name: toolName, args });
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[voice] tool exec failed:', toolName, error);
      modelOutput = { error: error?.message || 'Tool execution failed' };
    }

    // Return the tool result to the model, then ask it to respond.
    this.sendEvent({
      type: 'conversation.item.create',
      item: {
        type: 'function_call_output',
        call_id: callId,
        output: JSON.stringify(modelOutput),
      },
    });
    this.expectingClientResponse = true;
    this.sendEvent({ type: 'response.create' });
    this.setPhase('thinking');
  }

  /** User spoke over Spacey — stop playback, cancel response, start a new turn. */
  handleUserBargeIn() {
    if (this.isConnectGreeting() || this.isInAssistantEchoCooldown()) return;
    const responseId = this.activeResponseId;
    if (responseId) {
      this.responseStatuses.set(responseId, 'cancelled');
      if (this.pendingAssistantCommit?.id === responseId) {
        this.pendingAssistantCommit = null;
      }
    }
    this.userBargeInActive = true;
    this.stopAssistantPlayback();
    this.sendEvent({ type: 'response.cancel' });
    this.assistantTranscript = '';
    this.activeResponseId = null;
    this.setPhase('listening');
  }

  stopAssistantPlayback() {
    if (this.audioEl) {
      try {
        this.audioEl.pause();
      } catch { /* noop */ }
    }
    this.sendEvent({ type: 'output_audio_buffer.clear' });
  }

  resumeAssistantPlayback() {
    if (this.audioEl) {
      try {
        const playPromise = this.audioEl.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          playPromise.catch(() => {});
        }
      } catch { /* noop */ }
    }
  }

  setPhase(phase) {
    if (this.closed) return;
    this.phase = phase;
    this.onPhase(phase);
    this.applyMicGate();
  }

  /** Mic open while listening or during normal speech (barge-in). Off for greeting/thinking/search/cooldown. */
  applyMicGate() {
    const micOpen = !this.userMuted
      && (this.phase === 'listening' || this.phase === 'speaking')
      && this.phase !== 'greeting'
      && !this.isConnectGreeting()
      && !this.isInAssistantEchoCooldown();
    this.setMicTracksEnabled(micOpen);
  }

  setMicTracksEnabled(enabled) {
    if (!this.micStream) return;
    this.micStream.getAudioTracks().forEach((track) => {
      track.enabled = enabled;
    });
  }

  setMuted(muted) {
    this.userMuted = muted;
    this.applyMicGate();
  }

  // Releases media/network resources without emitting a status change.
  releaseResources() {
    this.closed = true;
    this.clearGreetingTimers();
    if (this.echoCooldownTimer) {
      clearTimeout(this.echoCooldownTimer);
      this.echoCooldownTimer = null;
    }
    this.pendingGreetingResponse = false;
    this.userBargeInActive = false;
    this.greetingAudioHeard = false;
    this.awaitingConnectGreeting = false;
    this.greetingResponseId = null;
    this.pendingGreetingCaption = null;
    this.clearGreetingPlaybackFallbackTimer();
    if (this.levelRaf) {
      try { window.cancelAnimationFrame(this.levelRaf); } catch { /* noop */ }
      this.levelRaf = null;
    }
    try {
      if (this.audioCtx) this.audioCtx.close();
    } catch { /* noop */ }
    this.audioCtx = null;
    this.micAnalyser = null;
    this.remoteAnalyser = null;
    try {
      if (this.dc) this.dc.close();
    } catch { /* noop */ }
    try {
      if (this.micStream) this.micStream.getTracks().forEach((t) => t.stop());
    } catch { /* noop */ }
    try {
      if (this.pc) this.pc.close();
    } catch { /* noop */ }
    if (this.audioEl) {
      this.audioEl.srcObject = null;
      this.audioEl = null;
    }
    this.dc = null;
    this.pc = null;
    this.micStream = null;
  }

  // User-initiated end of the session.
  stop() {
    this.releaseResources();
    this.onStatus('closed');
  }
}
